// «Abono a capital» del expediente: cotiza, captura el monto, confirma y pide el cargo al motor.
// Solo aparece con `loan.credit.prepay` y con el crédito activo.
import { useId, useState, type ReactNode } from 'react';
import { HandCoins } from 'lucide-react';
import type { LoanCreditStatusEnum } from '@fiado/type-kit/bin/loanCredit/enums/LoanCreditStatusEnum';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ErrorState, Loading, usePermissions } from '@/kit';
import type { LoanAdHocChargeRequested, LoanPrepaymentQuote } from '@/api/services/loan-credit';
import { useApplyPrepayment, usePrepaymentQuote } from '../api/usePrepayment';
import { formatMoneyCents } from '../logic';
import {
    canOfferPrepayment,
    CREDIT_PREPAY_PERMISSION,
    isPrepaymentAvailable,
    prepaymentAmountError,
    prepaymentErrorMessage,
    prepaymentRangeOf,
    typedPesosToCents,
} from '../prepayment';

/** Botón compacto junto al id del crédito, con el aire de los demás de esa esquina. */
const ACTION_BUTTON =
    'inline-flex flex-none items-center gap-1 whitespace-nowrap rounded-[5px] border border-border bg-card px-2 py-0.5 text-[10.5px] font-semibold text-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

const FIELD_LABEL = 'text-[10px] font-bold uppercase tracking-[0.04em] text-muted-foreground';

export function CreditPrepaymentAction({
    creditId,
    status,
}: {
    creditId: string;
    status: LoanCreditStatusEnum;
}) {
    const { hasPermission } = usePermissions();
    if (!hasPermission(CREDIT_PREPAY_PERMISSION) || !canOfferPrepayment(status)) return null;
    return <PrepaymentFlow creditId={creditId} />;
}

/** Monta las consultas aparte para no pedir QueryClient a quien no puede abonar. */
function PrepaymentFlow({ creditId }: { creditId: string }) {
    const [open, setOpen] = useState(false);
    return (
        <>
            <button
                type="button"
                className={ACTION_BUTTON}
                onClick={() => {
                    setOpen(true);
                }}
            >
                <HandCoins className="size-3" aria-hidden />
                Abono a capital
            </button>
            <Dialog open={open} onOpenChange={setOpen}>
                {open ? (
                    <PrepaymentDialogBody
                        creditId={creditId}
                        onClose={() => {
                            setOpen(false);
                        }}
                    />
                ) : null}
            </Dialog>
        </>
    );
}

type Step = 'amount' | 'confirm' | 'done';

function PrepaymentDialogBody({ creditId, onClose }: { creditId: string; onClose: () => void }) {
    const quote = usePrepaymentQuote(creditId);
    const apply = useApplyPrepayment(creditId);
    const [step, setStep] = useState<Step>('amount');
    const [amountText, setAmountText] = useState('');

    let body: ReactNode;
    let footer: ReactNode = (
        <Button variant="outline" onClick={onClose}>
            Cerrar
        </Button>
    );

    if (quote.isLoading) {
        body = <Loading size="sm" text="Cotizando el abono…" />;
    } else if (quote.error || !quote.data) {
        body = (
            <ErrorState
                title="No se pudo cotizar el abono"
                description={prepaymentErrorMessage(quote.error, 'quote')}
                onRetry={() => {
                    void quote.refetch();
                }}
            />
        );
    } else if (step === 'done' && apply.data) {
        body = <PrepaymentRequested charge={apply.data} />;
    } else {
        const range = prepaymentRangeOf(quote.data);
        const amountCents = typedPesosToCents(amountText);
        const amountError = prepaymentAmountError(amountCents, range);
        const available = isPrepaymentAvailable(range);

        if (step === 'confirm' && amountCents !== null) {
            body = (
                <ConfirmStep
                    creditId={creditId}
                    amountCents={amountCents}
                    error={apply.error ? prepaymentErrorMessage(apply.error, 'apply') : null}
                />
            );
            footer = (
                <>
                    <Button
                        variant="outline"
                        disabled={apply.isPending}
                        onClick={() => {
                            apply.reset();
                            setStep('amount');
                        }}
                    >
                        Atrás
                    </Button>
                    <Button
                        disabled={apply.isPending}
                        onClick={() => {
                            apply.mutate(amountCents, {
                                onSuccess: () => {
                                    setStep('done');
                                },
                            });
                        }}
                    >
                        {apply.isPending ? 'Aplicando…' : 'Aplicar abono'}
                    </Button>
                </>
            );
        } else {
            body = (
                <AmountStep
                    quote={quote.data}
                    available={available}
                    amountText={amountText}
                    error={amountText === '' ? null : amountError}
                    onAmountChange={setAmountText}
                />
            );
            footer = (
                <>
                    <Button variant="outline" onClick={onClose}>
                        Cancelar
                    </Button>
                    <Button
                        disabled={!available || amountError !== null}
                        onClick={() => {
                            setStep('confirm');
                        }}
                    >
                        Continuar
                    </Button>
                </>
            );
        }
    }

    return (
        <DialogContent className="max-w-md">
            <DialogHeader>
                <DialogTitle className="text-base">Abono a capital</DialogTitle>
                <DialogDescription className="text-[11.5px]">{`Crédito ${creditId}`}</DialogDescription>
            </DialogHeader>
            <div className="min-w-0 space-y-3">{body}</div>
            <DialogFooter className="gap-2">{footer}</DialogFooter>
        </DialogContent>
    );
}

/** Los cuatro datos de la cotización y el monto a capturar. */
function AmountStep({
    quote,
    available,
    amountText,
    error,
    onAmountChange,
}: {
    quote: LoanPrepaymentQuote;
    available: boolean;
    amountText: string;
    error: string | null;
    onAmountChange: (text: string) => void;
}) {
    const fieldId = useId();
    const range = prepaymentRangeOf(quote);
    const facts: [string, string, string?][] = [
        ['Saldo insoluto', formatMoneyCents(quote.outstandingCapitalCents)],
        ['Pago del periodo', formatMoneyCents(quote.periodPaymentCents)],
        ['Abono mínimo', formatMoneyCents(range.minCents), 'Lo fija el plan del crédito'],
        [
            'Abono máximo',
            formatMoneyCents(range.maxCents),
            `La liquidación anticipada es de ${formatMoneyCents(quote.settlementAmountCents)}`,
        ],
    ];
    return (
        <>
            <dl className="grid grid-cols-2 gap-2">
                {facts.map(([label, value, hint]) => (
                    <div
                        key={label}
                        className="rounded-md border border-border bg-muted/30 px-2.5 py-2"
                    >
                        <dt className={FIELD_LABEL}>{label}</dt>
                        <dd className="m-0 mt-0.5 text-[14px] font-bold tabular-nums">{value}</dd>
                        {hint ? (
                            <dd className="m-0 mt-0.5 text-[10.5px] leading-snug text-muted-foreground">
                                {hint}
                            </dd>
                        ) : null}
                    </div>
                ))}
            </dl>

            {available ? (
                <div className="space-y-1">
                    <label className={FIELD_LABEL} htmlFor={`${fieldId}-amount`}>
                        Monto del abono ($)
                    </label>
                    <Input
                        id={`${fieldId}-amount`}
                        type="number"
                        inputMode="decimal"
                        step={0.01}
                        min={range.minCents / 100}
                        max={range.maxCents / 100}
                        value={amountText}
                        aria-invalid={error !== null}
                        aria-describedby={`${fieldId}-amount-hint`}
                        onChange={(event) => {
                            onAmountChange(event.target.value);
                        }}
                    />
                    <p
                        id={`${fieldId}-amount-hint`}
                        role={error === null ? undefined : 'alert'}
                        className={
                            error === null
                                ? 'text-[10.5px] leading-snug text-muted-foreground'
                                : 'text-[10.5px] font-semibold leading-snug text-destructive'
                        }
                    >
                        {error ??
                            `De ${formatMoneyCents(range.minCents)} a ${formatMoneyCents(range.maxCents)}.`}
                    </p>
                </div>
            ) : (
                <p role="alert" className="text-[11.5px] font-semibold text-destructive">
                    Este crédito no admite abono por ahora. Ofrece la liquidación anticipada.
                </p>
            )}
        </>
    );
}

function ConfirmStep({
    creditId,
    amountCents,
    error,
}: {
    creditId: string;
    amountCents: number;
    error: string | null;
}) {
    return (
        <>
            <p className="text-[12px] leading-relaxed text-foreground">
                Se pedirá al motor de cobro un abono a capital de{' '}
                <strong className="font-semibold tabular-nums">
                    {formatMoneyCents(amountCents)}
                </strong>{' '}
                para el crédito <span className="font-mono">{creditId}</span>.
            </p>
            <p className="text-[11.5px] leading-relaxed text-muted-foreground">
                Si se cobra completo, baja el capital y la tabla se recalcula con el mismo número de
                cuotas y las mismas fechas. Si no se cobra completo, lo cobrado pasa a revisión.
            </p>
            {error === null ? null : (
                <p
                    role="alert"
                    className="rounded-md bg-destructive/10 px-3 py-2 text-[11.5px] font-semibold text-destructive"
                >
                    {error}
                </p>
            )}
        </>
    );
}

function PrepaymentRequested({ charge }: { charge: LoanAdHocChargeRequested }) {
    return (
        <div role="status" className="space-y-1.5">
            <p className="text-[12.5px] font-semibold text-success">
                {`Abono de ${formatMoneyCents(charge.amountCents)} pedido al motor de cobro.`}
            </p>
            <p className="text-[11.5px] leading-relaxed text-muted-foreground">
                {`Cargo ${charge.chargeId}. El expediente se actualiza cuando llegue el resultado del cobro.`}
            </p>
        </div>
    );
}
